export const DRAFTS_STORAGE_KEY = 'yugui_publish_drafts_v2';
const LEGACY_DRAFT_KEY = 'yugui_publish_draft_v1';
const MAX_DRAFTS = 50;
const MAX_STORED_IMG = 450000;

export const defaultPublishFormData = () => ({
  title: '',
  content: '',
  name: '',
  petType: '',
  age: '',
  gender: '公',
  img: '',
  rawFile: null,
  isVideo: false,
  health: { vaccine: false, deworm: false, neuter: false },
  tags: [],
  type: 'post',
  visibility: 'public'
});

export const suggestDraftName = (formData) => {
  if (!formData) return '未命名草稿';
  if (formData.type === 'adopt') {
    return (formData.name || formData.petType || '领养草稿').trim().slice(0, 40) || '领养草稿';
  }
  const fromTitle = (formData.title || '').trim();
  if (fromTitle) return fromTitle.slice(0, 40);
  const fromContent = (formData.content || '').trim().replace(/\s+/g, ' ');
  if (fromContent) return fromContent.slice(0, 24) + (fromContent.length > 24 ? '…' : '');
  return '日常草稿';
};

export const getDraftPreviewText = (draft) => {
  const fd = draft?.formData || {};
  if (fd.type === 'adopt') {
    return fd.content || fd.petType || '领养信息草稿';
  }
  return fd.content || fd.title || '无正文';
};

export const sanitizeFormDataForStorage = (formData) => {
  const img = formData?.img || '';
  const isVid =
    !!formData?.isVideo ||
    (typeof img === 'string' && (img.startsWith('data:video') || img.startsWith('blob:')));
  let droppedMedia = false;
  let storedImg = '';
  if (typeof img === 'string' && img.startsWith('blob:')) {
    droppedMedia = true;
  } else if (isVid) {
    droppedMedia = true;
  } else if (img && img.length <= MAX_STORED_IMG) {
    storedImg = img;
  } else if (img) {
    droppedMedia = true;
  }
  return {
    sanitized: {
      ...defaultPublishFormData(),
      ...formData,
      type: formData?.type === 'adopt' ? 'adopt' : 'post',
      health:
        formData?.health && typeof formData.health === 'object'
          ? formData.health
          : defaultPublishFormData().health,
      tags: Array.isArray(formData?.tags) ? formData.tags : [],
      rawFile: null,
      img: storedImg,
      isVideo: false
    },
    droppedMedia
  };
};

export const formDataFromPost = (post) => {
  if (!post) {
    return { formData: defaultPublishFormData(), droppedMedia: false };
  }
  const img = post.img || '';
  const isVid = !!(post.isVideo || (typeof img === 'string' && img.startsWith('data:video')));
  const formData = {
    title: post.title || '',
    content: post.content || post.description || '',
    tags: Array.isArray(post.tags) ? post.tags : [],
    visibility: post.visibility || 'public',
    type: post.type === 'adopt' ? 'adopt' : 'post',
    name: post.name || '',
    petType: post.petType || '',
    age: post.age || '',
    gender: post.gender || '公',
    health: post.health || { vaccine: false, deworm: false, neuter: false },
    img: !isVid && img.length <= MAX_STORED_IMG ? img : '',
    isVideo: false,
    rawFile: null
  };
  return {
    formData,
    droppedMedia: isVid || img.length > MAX_STORED_IMG
  };
};

const createDraftId = () =>
  `draft_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const readDraftsRaw = () => {
  try {
    const raw = localStorage.getItem(DRAFTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeDraftsRaw = (drafts) => {
  localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts.slice(0, MAX_DRAFTS)));
};

export const migrateLegacyPublishDraft = () => {
  try {
    const legacyRaw = localStorage.getItem(LEGACY_DRAFT_KEY);
    if (!legacyRaw) return false;
    const parsed = JSON.parse(legacyRaw);
    const saved = parsed?.formData;
    if (saved && typeof saved === 'object') {
      const { sanitized } = sanitizeFormDataForStorage(saved);
      const now = parsed?.savedAt || Date.now();
      const drafts = readDraftsRaw();
      drafts.unshift({
        id: createDraftId(),
        name: suggestDraftName(sanitized),
        formData: sanitized,
        savedAt: now,
        updatedAt: now
      });
      writeDraftsRaw(drafts);
    }
    localStorage.removeItem(LEGACY_DRAFT_KEY);
    return true;
  } catch {
    return false;
  }
};

export const loadPublishDrafts = () => {
  migrateLegacyPublishDraft();
  return loadPublishDraftsLocal();
};

export const loadPublishDraftsLocal = () => {
  migrateLegacyPublishDraft();
  return readDraftsRaw()
    .filter((d) => d && d.id && d.formData)
    .sort((a, b) => (b.updatedAt || b.savedAt || 0) - (a.updatedAt || a.savedAt || 0));
};

export const savePublishDraftsLocal = (drafts) => {
  writeDraftsRaw(drafts);
};

export const getPublishDraft = (id) =>
  loadPublishDraftsLocal().find((d) => String(d.id) === String(id)) || null;

export const upsertPublishDraft = ({ id, name, formData }) => {
  const { sanitized, droppedMedia } = sanitizeFormDataForStorage(formData);
  const now = Date.now();
  const drafts = loadPublishDraftsLocal();
  const draftName = (name || suggestDraftName(sanitized)).trim().slice(0, 60) || '未命名草稿';
  let draft;
  let isNew = false;

  if (id) {
    const idx = drafts.findIndex((d) => String(d.id) === String(id));
    if (idx >= 0) {
      draft = {
        ...drafts[idx],
        name: draftName,
        formData: sanitized,
        updatedAt: now
      };
      drafts[idx] = draft;
    } else {
      isNew = true;
      draft = { id: String(id), name: draftName, formData: sanitized, savedAt: now, updatedAt: now };
      drafts.unshift(draft);
    }
  } else {
    isNew = true;
    draft = {
      id: createDraftId(),
      name: draftName,
      formData: sanitized,
      savedAt: now,
      updatedAt: now
    };
    drafts.unshift(draft);
  }

  writeDraftsRaw(drafts);
  return { draft, droppedMedia, isNew };
};

export const renamePublishDraft = (id, name) => {
  const trimmed = (name || '').trim().slice(0, 60);
  if (!trimmed) return null;
  const drafts = loadPublishDraftsLocal();
  const idx = drafts.findIndex((d) => String(d.id) === String(id));
  if (idx < 0) return null;
  drafts[idx] = { ...drafts[idx], name: trimmed, updatedAt: Date.now() };
  writeDraftsRaw(drafts);
  return drafts[idx];
};

export const deletePublishDrafts = (ids) => {
  const idSet = new Set((ids || []).map(String));
  if (!idSet.size) return 0;
  const drafts = loadPublishDrafts().filter((d) => !idSet.has(String(d.id)));
  const removed = loadPublishDrafts().length - drafts.length;
  writeDraftsRaw(drafts);
  return removed;
};

export const clearAllPublishDrafts = () => {
  try {
    localStorage.removeItem(DRAFTS_STORAGE_KEY);
    localStorage.removeItem(LEGACY_DRAFT_KEY);
    return true;
  } catch {
    return false;
  }
};

/** @deprecated 兼容旧调用，仅清除 legacy 单条草稿 */
export const clearPublishDraftStorage = () => {
  try {
    localStorage.removeItem(LEGACY_DRAFT_KEY);
    return true;
  } catch {
    return false;
  }
};

export const validateDraftForPublish = (formData) => {
  if (!formData?.img) return '缺少图片或视频';
  if (typeof formData.img === 'string' && formData.img.startsWith('blob:')) {
    return '媒体已失效，请重新上传';
  }
  if (formData.type === 'adopt') {
    if (!formData.name?.trim()) return '缺少宠物名称';
  } else if (!formData.title?.trim()) {
    return '缺少标题';
  }
  if (!formData.content?.trim()) return '缺少正文';
  return null;
};
